import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { trackIntent } from '../utils/metaPixel';
interface HeroSectionProps {
  onOpenForm: () => void;
}
export function HeroSection({
  onOpenForm
}: HeroSectionProps) {
  const scrollToManifesto = () => {
    // The Manifesto is the second <section> on the page (Hero is first).
    const sections = document.querySelectorAll('section');
    const manifesto = sections[1];
    manifesto?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const trustItems = ['FCRA Section 611 compliant', 'CFPB-aligned methodology', 'Independent editorial team'];
  return <section className="relative bg-white pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden" data-id="element-256">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-257">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#0F2747 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} data-id="element-258"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-259">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center" data-id="element-260">
          {/* Left Column: Copy */}
          <div className="max-w-2xl text-left" data-id="element-261">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.05
          }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald/10 border border-emerald/20 mb-8" data-id="element-262">
              <span className="text-emerald text-xs font-bold tracking-widest uppercase" data-id="element-263">
                The Method
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-navy leading-[1.1] mb-6 tracking-tight" data-id="element-264">
              Rebuild Your Credit.
              <br data-id="element-265" />
              The Honest Way.
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg sm:text-xl text-slate mb-5 leading-[1.6] font-medium max-w-xl" data-id="element-266">
              We teach the same federal methods the bureaus use against you — so
              you can use them back. No gurus. No shortcuts. Just the law,
              applied correctly.
            </motion.p>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="text-base sm:text-lg text-gold italic mb-10 max-w-xl font-medium" data-id="element-267">
              The goal is not panic. It is visibility.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8" data-id="element-268">
              <button onClick={() => {
              trackIntent('hero_playbook');
              onOpenForm();
            }} className="w-full sm:w-auto bg-emerald hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-cta hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center" data-id="element-269">
                Get The Playbook
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-270" />
              </button>
              <button onClick={scrollToManifesto} className="w-full sm:w-auto text-navy font-bold text-lg hover:text-emerald transition-colors flex items-center justify-center group" data-id="element-271">
                <span className="border-b-2 border-gold pb-0.5 group-hover:border-emerald transition-colors" data-id="element-272">
                  Read The Manifesto
                </span>
              </button>
            </motion.div>

            {/* Trust Strip */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate font-medium" data-id="element-273">
              {trustItems.map((item, index) => <Fragment key={item} data-id="element-274">
                  <span className="inline-flex items-center" data-id="element-275">
                    <Check className="w-3.5 h-3.5 text-emerald mr-1.5 flex-shrink-0" aria-hidden="true" data-id="element-276" />
                    {item}
                  </span>
                  {index < trustItems.length - 1 && <span className="w-1 h-1 rounded-full bg-gold flex-shrink-0" aria-hidden="true" data-id="element-277"></span>}
                </Fragment>)}
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="text-xs text-slate-500 italic mt-3" data-id="element-278">
              Methodology updated May 2026 · Reviewed against current CFPB
              complaint patterns
            </motion.p>
          </div>

          {/* Right Column: Method in Action — Before / After Card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative lg:ml-auto w-full max-w-md mx-auto" data-id="element-279">
            <div className="bg-white rounded-2xl shadow-card border-t-4 border-gold border-x border-b border-slate-100 overflow-hidden" data-id="element-280">
              {/* Card Header */}
              <div className="px-6 pt-6 pb-4 text-center border-b border-slate-100" data-id="element-281">
                <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500" data-id="element-282">
                  Method in Action
                </span>
                <h3 className="text-lg font-bold text-navy mt-1" data-id="element-283">
                  What the framework looks like
                </h3>
              </div>

              {/* Before / After Comparison */}
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-8" data-id="element-284">
                {/* Before */}
                <div className="text-center" data-id="element-285">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 border-2 border-red-200 mb-3" data-id="element-286">
                    <span className="text-3xl font-extrabold text-red-700" data-id="element-287">
                      580
                    </span>
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-700 mb-1" data-id="element-288">
                    Before
                  </p>
                  <p className="text-xs text-slate leading-relaxed" data-id="element-289">
                    Late payments,
                    <br data-id="element-290" />
                    errors reported
                  </p>
                </div>

                {/* Arrow */}
                <div className="flex flex-col items-center justify-center" data-id="element-291">
                  <ArrowRight className="w-7 h-7 text-gold" strokeWidth={2.5} data-id="element-292" />
                  <span className="font-mono text-[9px] text-gold tracking-widest uppercase mt-1" data-id="element-293">
                    Method
                  </span>
                </div>

                {/* After */}
                <div className="text-center" data-id="element-294">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 mb-3" data-id="element-295">
                    <span className="text-3xl font-extrabold text-emerald-700" data-id="element-296">
                      720
                    </span>
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1" data-id="element-297">
                    After
                  </p>
                  <p className="text-xs text-slate leading-relaxed" data-id="element-298">
                    Errors removed,
                    <br data-id="element-299" />
                    positive accounts added
                  </p>
                </div>
              </div>

              {/* Result Badge */}
              <div className="px-6 pb-6" data-id="element-300">
                <div className="bg-offwhite rounded-lg px-4 py-3 text-center border border-slate-100" data-id="element-301">
                  <span className="text-sm font-bold text-navy block" data-id="element-302">
                    Average member result in 90 days
                    <span className="text-gold align-super text-xs" data-id="element-303">*</span>
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mt-3 text-center italic" data-id="element-304">
                  <span className="text-gold not-italic" data-id="element-305">*</span> Results vary.
                  Not all consumers achieve identical outcomes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}